import React from 'react';
import { useDispatch } from 'react-redux';
import { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-premium';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { Icon, showDeleteDialog, useMountEffect, useSettings } from 'app/main/components';
import { DetailsAdditionalPropsContext, ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';
import type { DetailsPageContentProps } from 'app/main/components/page-layouts';
import { showItemsPricingsEditDialog } from '../../components/ItemsPricingsEditDialog';

export default function ItemsPricingsTab(props: DetailsPageContentProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);

	const isEditable = authorizer.can('update', 'items_pricings');

	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	const { changeAdditionalProp } = React.useContext(DetailsAdditionalPropsContext);

	function openAddDialog({ baseModel, isAdd }: { baseModel: any; isAdd?: boolean }) {
		dispatch(showItemsPricingsEditDialog({ baseModel, isAdd, updateRefresh }));
	}

	function openDeleteDialog({ model }: { model: any }) {
		dispatch(
			showDeleteDialog({
				module: 'items_pricings',
				id: model.itpr_uid,
				replace: false,
				hardDelete: true,
				updateRefresh
			})
		);
	}

	const columns: GridColDef[] = [
		{
			field: 'pric_title',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_PRICING_GROUP'),
			minWidth: 200,
			renderCell: ViewTableUtils.renderCellWithTooltip,
			hideable: false
		},
		{
			field: 'itpr_d_from',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_DATE_FROM'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'itpr_d_to',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_DATE_TO'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderDate,
			hideable: false
		},
		{
			field: 'comp_title',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_COMPANY'),
			minWidth: 300,
			flex: 1,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'companies', uid: row.itpr_comp_uid } }),
			hideable: false
		},
		{
			field: 'itpr_qty_min',
			type: 'number',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_ORDSMINQTY'),
			minWidth: 125
		},
		{
			field: 'itpr_base_price',
			type: 'number',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_BASE_PRICE'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderCurrency,
			hideable: false
		},
		{
			field: 'itpr_discountable',
			type: 'boolean',
			headerName: t('DETAILS_ITEMS_PRICINGS_FIELD_DISCOUNTABLE'),
			minWidth: 125
		}
	];

	if (isEditable) {
		columns.push({
			field: 'actions',
			type: 'actions',
			width: 80,
			getActions: ({ row }) => [
				<GridActionsCellItem
					label={t('VIEW_ITEM_BUTTON_EDIT')}
					icon={<Icon icon="edit" />}
					onClick={() => openAddDialog({ baseModel: row })}
				/>,
				<GridActionsCellItem
					label={t('VIEW_ITEM_BUTTON_DELETE')}
					icon={<Icon icon="delete" />}
					onClick={() => openDeleteDialog({ model: row })}
				/>
			]
		});
	}

	useMountEffect(() => {
		changeAdditionalProp('openPricingAddDialog', () =>
			openAddDialog({ baseModel: { itpr_item_uid: props.model.item_uid }, isAdd: true })
		);
	});

	return (
		<ViewTable
			module="items_pricings"
			columns={columns}
			defaultFilters={[
				{
					filt_field: 'itpr_item_uid',
					filt_clause: 'equals',
					filt_value: props.model.item_uid
				}
			]}
			refresh={refresh}
			updateRefresh={updateRefresh}
		/>
	);
}
